import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeft, Download } from "lucide-react";
import {
  BUILT_IN_USER_TYPE_IDS,
  canAccessClient,
  type CustomFieldDefinition,
  type SessionDto,
  type UserDetailDto,
} from "@platform/core";
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  DataGrid,
  Field,
  Input,
  MultiSelect,
  NativeSelect,
  Skeleton,
  Tabs,
  Textarea,
  useToast,
  type GridColDef,
} from "@platform/ui";
import { useApi, useConsole } from "../../context";
import { UserTypeBadge, accessSummary, groupOptions, useGroups, useUserTypes } from "../../lib/access";
import { useT } from "../../i18n";
import { downloadText } from "../../lib/download";
import { errorText } from "../../lib/errors";
import { formatDate, formatDateTime } from "../../lib/format";
import { CopyButton } from "../../lib/copy";
import { useGridText } from "../../lib/grid";
import { ConfirmDialog } from "../../layout/ConfirmDialog";
import { ErrorBanner, PageCard, Section } from "../../layout/Page";
import { AuditTable } from "../AuditLogsPage";
import { IdentifiersTab } from "./IdentifiersTab";
import { SetPasswordDialog } from "./SetPasswordDialog";
import { Avatar, StatusBadge } from "./UsersPage";

type CustomValue = string | number | boolean | null;

function CustomFieldInput({
  def,
  value,
  onChange,
  locale,
  disabled,
}: {
  def: CustomFieldDefinition;
  value: CustomValue | undefined;
  onChange: (v: CustomValue) => void;
  locale: "th" | "en";
  disabled: boolean;
}) {
  const label = def.label[locale];
  if (def.type === "boolean")
    return (
      <Checkbox
        className="mb-4"
        label={label}
        checked={value === true}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
    );
  return (
    <Field label={label} required={def.required}>
      {(p) =>
        def.type === "select" ? (
          <NativeSelect
            {...p}
            disabled={disabled}
            value={typeof value === "string" ? value : ""}
            onChange={(e) => onChange(e.target.value || null)}
          >
            <option value="">—</option>
            {(def.options ?? []).map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </NativeSelect>
        ) : (
          <Input
            {...p}
            disabled={disabled}
            type={def.type === "number" ? "number" : def.type === "date" ? "date" : "text"}
            value={value === null || value === undefined ? "" : String(value)}
            onChange={(e) =>
              onChange(
                e.target.value === ""
                  ? null
                  : def.type === "number"
                    ? Number(e.target.value)
                    : e.target.value,
              )
            }
          />
        )
      }
    </Field>
  );
}

function ProfileTab({ user }: { user: UserDetailDto }) {
  const { t, locale } = useT();
  const api = useApi();
  const { can } = useConsole();
  const toast = useToast();
  const qc = useQueryClient();
  const fields = useQuery({
    queryKey: ["settings", "custom-fields"],
    queryFn: () => api.settings.get("custom-fields"),
    enabled: can("settings:read"),
  });
  const initial = () => ({
    displayName: user.displayName,
    employeeCode: user.profile?.employeeCode ?? "",
    firstName: user.profile?.firstName ?? "",
    lastName: user.profile?.lastName ?? "",
    department: user.profile?.department ?? "",
    position: user.profile?.position ?? "",
    custom: { ...(user.profile?.custom ?? {}) } as Record<string, CustomValue>,
  });
  // The parent re-mounts this tab (key) when the user record changes, so the initial state stays fresh.
  const [form, setForm] = useState(initial);
  const editable = can("users:write");
  const save = useMutation({
    mutationFn: () =>
      api.users.update(user.id, {
        displayName: form.displayName,
        profile: {
          employeeCode: form.employeeCode || null,
          firstName: form.firstName || null,
          lastName: form.lastName || null,
          department: form.department || null,
          position: form.position || null,
          custom: form.custom,
        },
      }),
    onSuccess: (u) => {
      qc.setQueryData(["user", user.id], u);
      void qc.invalidateQueries({ queryKey: ["users"] });
      toast({ title: t("common.saved"), tone: "success" });
    },
  });
  const text = (
    key: "displayName" | "employeeCode" | "firstName" | "lastName" | "department" | "position",
    label: string,
  ) => (
    <Field label={label}>
      {(p) => (
        <Input
          {...p}
          disabled={!editable}
          value={form[key]}
          onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        />
      )}
    </Field>
  );
  return (
    <form
      className="px-6 py-5"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      {save.isError && (
        <Alert tone="error" className="mb-4">
          {errorText(save.error)}
        </Alert>
      )}
      <div className="grid gap-x-4 sm:grid-cols-2">
        {text("displayName", t("user.displayName"))}
        {text("employeeCode", t("user.employeeCode"))}
        {text("firstName", t("user.firstName"))}
        {text("lastName", t("user.lastName"))}
        {text("department", t("user.department"))}
        {text("position", t("user.position"))}
      </div>
      {fields.data && fields.data.value.length > 0 && (
        <>
          <h3 className="mt-2 mb-3 text-sm font-medium text-ink-2">{t("user.customFields")}</h3>
          <div className="grid gap-x-4 sm:grid-cols-2">
            {fields.data.value.map((def) => (
              <CustomFieldInput
                key={def.key}
                def={def}
                locale={locale}
                disabled={!editable}
                value={form.custom[def.key]}
                onChange={(v) => setForm({ ...form, custom: { ...form.custom, [def.key]: v } })}
              />
            ))}
          </div>
        </>
      )}
      {editable && (
        <div className="flex justify-end">
          <Button type="submit" loading={save.isPending}>
            {t("user.saveProfile")}
          </Button>
        </div>
      )}
    </form>
  );
}

function SecurityTab({ user }: { user: UserDetailDto }) {
  const { t } = useT();
  const api = useApi();
  const { can } = useConsole();
  const toast = useToast();
  const qc = useQueryClient();
  const [confirmMfa, setConfirmMfa] = useState(false);
  const [setPasswordOpen, setSetPasswordOpen] = useState(false);
  const resetMfa = useMutation({
    mutationFn: () => api.users.resetMfa(user.id),
    onSuccess: () => {
      setConfirmMfa(false);
      toast({ title: t("user.mfaReset"), tone: "success" });
      void qc.invalidateQueries({ queryKey: ["user", user.id] });
    },
  });
  const reset = useMutation({
    mutationFn: () => api.users.sendPasswordReset(user.id),
    onSuccess: () => toast({ title: t("user.passwordResetSent"), tone: "success" }),
  });
  return (
    <div>
      <Section
        title={t("user.mfa")}
        actions={
          can("users:write") && user.mfaEnabled ? (
            <Button variant="secondary" size="sm" onClick={() => setConfirmMfa(true)}>
              {t("user.resetMfa")}
            </Button>
          ) : null
        }
      >
        <Badge dot tone={user.mfaEnabled ? "success" : "neutral"}>
          {user.mfaEnabled ? t("user.mfaOn") : t("user.mfaOff")}
        </Badge>
      </Section>
      <Section
        title={t("user.password")}
        actions={
          can("users:write") ? (
            <div className="flex flex-wrap gap-2">
              <Button
                variant="secondary"
                size="sm"
                data-testid="set-password-button"
                onClick={() => setSetPasswordOpen(true)}
              >
                {t("user.setPassword.action")}
              </Button>
              <Button variant="secondary" size="sm" loading={reset.isPending} onClick={() => reset.mutate()}>
                {t("user.passwordReset")}
              </Button>
            </div>
          ) : null
        }
      >
        <p className="text-sm text-ink-2">{user.email}</p>
      </Section>
      <SetPasswordDialog user={user} open={setPasswordOpen} onOpenChange={setSetPasswordOpen} />
      <Section title={t("user.linkedProviders")}>
        <div className="flex flex-wrap gap-2">
          <Badge>{t("users.provider.password")}</Badge>
          {user.linkedProviders.map((p) => (
            <Badge key={p} tone="info">
              {p}
            </Badge>
          ))}
        </div>
      </Section>
      <ConfirmDialog
        open={confirmMfa}
        onOpenChange={setConfirmMfa}
        title={t("user.resetMfa")}
        confirmLabel={t("user.resetMfa")}
        destructive
        loading={resetMfa.isPending}
        onConfirm={() => resetMfa.mutate()}
      >
        <p className="text-sm">{t("user.resetMfaConfirm")}</p>
      </ConfirmDialog>
    </div>
  );
}

function RolesTab({ user }: { user: UserDetailDto }) {
  const { t } = useT();
  const api = useApi();
  const { can, me } = useConsole();
  const toast = useToast();
  const qc = useQueryClient();
  const roles = useQuery({ queryKey: ["roles"], queryFn: api.roles.list, enabled: can("roles:read") });
  const [selected, setSelected] = useState<string[]>(user.roles);
  useEffect(() => setSelected(user.roles), [user.roles]);
  const save = useMutation({
    mutationFn: () => api.users.setRoles(user.id, selected),
    onSuccess: () => {
      toast({ title: t("user.rolesSaved"), tone: "success" });
      void qc.invalidateQueries({ queryKey: ["user", user.id] });
      void qc.invalidateQueries({ queryKey: ["users"] });
    },
  });
  const editable = can("roles:write");
  return (
    <div className="px-6 py-5">
      <fieldset>
        <legend className="sr-only">{t("user.tab.roles")}</legend>
        <div className="space-y-3">
          {roles.data?.items.map((r) => (
            <Checkbox
              key={r.id}
              label={
                <span>
                  <span className="font-medium">{r.name}</span>
                  {r.description && <span className="ml-2 text-ink-2">— {r.description}</span>}
                </span>
              }
              checked={selected.includes(r.id)}
              disabled={!editable || (r.id === "owner" && !me.roles.includes("owner"))}
              onChange={(e) =>
                setSelected(e.target.checked ? [...selected, r.id] : selected.filter((x) => x !== r.id))
              }
            />
          ))}
        </div>
      </fieldset>
      {editable && (
        <div className="mt-4 flex justify-end">
          <Button loading={save.isPending} onClick={() => save.mutate()} data-testid="save-roles-button">
            {t("user.saveRoles")}
          </Button>
        </div>
      )}
    </div>
  );
}

function AccessTab({ user }: { user: UserDetailDto }) {
  const { t } = useT();
  const api = useApi();
  const { can, config } = useConsole();
  const toast = useToast();
  const qc = useQueryClient();
  const types = useUserTypes();
  const groups = useGroups();
  const clients = useQuery({
    queryKey: ["clients"],
    queryFn: api.clients.list,
    enabled: can("clients:read"),
  });
  const [type, setType] = useState(user.userType ?? "");
  const [confirmType, setConfirmType] = useState(false);
  const [selectedGroups, setSelectedGroups] = useState<string[]>(user.groups);
  useEffect(() => setType(user.userType ?? ""), [user.userType]);
  useEffect(() => setSelectedGroups(user.groups), [user.groups]);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["user", user.id] });
    void qc.invalidateQueries({ queryKey: ["users"] });
    void qc.invalidateQueries({ queryKey: ["user-types"] });
    void qc.invalidateQueries({ queryKey: ["groups"] });
  };
  const changeType = useMutation({
    mutationFn: () => api.users.update(user.id, { userType: type }),
    onSuccess: () => {
      setConfirmType(false);
      toast({ title: t("access.typeChanged"), tone: "success" });
      refresh();
    },
  });
  const saveGroups = useMutation({
    mutationFn: () => api.users.setGroups(user.id, selectedGroups),
    onSuccess: () => {
      toast({ title: t("access.groupsSaved"), tone: "success" });
      refresh();
    },
  });
  // The root account is a system account: no user type, no groups, console only (docs/root-account.md).
  const isRoot = user.userType === null;
  const typeIds = types.data?.items.map((x) => x.id) ?? BUILT_IN_USER_TYPE_IDS;
  const canType = can("users:write") && !isRoot;
  const canGroups = can("groups:write") && !isRoot;
  const sameGroups =
    selectedGroups.length === user.groups.length && selectedGroups.every((g) => user.groups.includes(g));
  return (
    <div>
      {isRoot ? (
        <Section title={t("access.userType")}>
          <Alert tone="info" data-testid="root-system-account">
            {t("access.rootSystem")}
          </Alert>
        </Section>
      ) : (
        <>
          <Section title={t("access.userType")}>
            <p className="mb-3 text-sm text-ink-2">{t("access.typeHint")}</p>
            <div className="flex flex-wrap items-end gap-2">
              <Field label={t("access.userType")} className="mb-0 min-w-48">
                {(p) => (
                  <NativeSelect
                    {...p}
                    data-testid="user-type-select"
                    disabled={!canType}
                    value={type}
                    onChange={(e) => setType(e.target.value)}
                  >
                    {[...new Set([type, ...typeIds])].map((id) => (
                      <option key={id} value={id}>
                        {id}
                      </option>
                    ))}
                  </NativeSelect>
                )}
              </Field>
              {canType && (
                <Button
                  variant="secondary"
                  data-testid="change-user-type"
                  disabled={type === user.userType}
                  onClick={() => {
                    changeType.reset();
                    setConfirmType(true);
                  }}
                >
                  {t("access.changeType")}
                </Button>
              )}
            </div>
          </Section>
          {can("groups:read") && (
            <Section title={t("access.groups")}>
              <p className="mb-3 text-sm text-ink-2">{t("access.groupsHint")}</p>
              {saveGroups.isError && (
                <Alert tone="error" className="mb-3">
                  {errorText(saveGroups.error)}
                </Alert>
              )}
              <div className="flex flex-wrap items-end gap-2">
                <Field label={t("access.groups")} className="mb-0 min-w-64 flex-1">
                  {(p) => (
                    <MultiSelect
                      {...p}
                      data-testid="user-groups-select"
                      label={t("access.groups")}
                      disabled={!canGroups}
                      value={selectedGroups}
                      onChange={setSelectedGroups}
                      options={groupOptions(groups.data?.items)}
                      placeholder={t("common.none")}
                      emptyText={t("groups.empty")}
                      searchPlaceholder={t("common.search")}
                    />
                  )}
                </Field>
                {canGroups && (
                  <Button
                    data-testid="save-user-groups"
                    loading={saveGroups.isPending}
                    disabled={sameGroups}
                    onClick={() => saveGroups.mutate()}
                  >
                    {t("access.saveGroups")}
                  </Button>
                )}
              </div>
            </Section>
          )}
        </>
      )}
      {can("clients:read") && (
        <Section title={t("access.applications")}>
          <p className="mb-3 text-sm text-ink-2">{t("access.applicationsHint")}</p>
          {clients.isError ? (
            <ErrorBanner error={clients.error} onRetry={() => void clients.refetch()} />
          ) : clients.isPending ? (
            <Skeleton className="h-10" />
          ) : (
            <ul className="divide-y divide-line" data-testid="user-applications">
              {clients.data.items.map((c) => {
                const ok = isRoot
                  ? c.clientId === config.clientId
                  : canAccessClient(c.access, { userType: user.userType, groups: user.groups });
                return (
                  <li key={c.clientId} className="flex flex-wrap items-center gap-2 py-2 text-sm">
                    <Link
                      to="/oauth-clients/$clientId"
                      params={{ clientId: c.clientId }}
                      className="font-medium text-primary-dark hover:underline"
                    >
                      {c.name}
                    </Link>
                    <span className="text-xs text-ink-2">{accessSummary(c.access, t)}</span>
                    <span className="flex-1" />
                    {c.disabled && <Badge tone="warning">{t("common.disabled")}</Badge>}
                    <Badge dot tone={ok ? "success" : "neutral"}>
                      {ok ? t("access.canSignIn") : t("access.cannotSignIn")}
                    </Badge>
                  </li>
                );
              })}
            </ul>
          )}
        </Section>
      )}
      <ConfirmDialog
        open={confirmType}
        onOpenChange={setConfirmType}
        title={t("access.changeTypeTitle")}
        confirmLabel={t("access.changeType")}
        confirmTestId="confirm-user-type"
        loading={changeType.isPending}
        onConfirm={() => changeType.mutate()}
      >
        <p className="text-sm">
          {t("access.changeTypeBody", { from: user.userType ?? "", to: type, email: user.email })}
        </p>
        {changeType.isError && (
          <Alert tone="error" className="mt-3">
            {errorText(changeType.error)}
          </Alert>
        )}
      </ConfirmDialog>
    </div>
  );
}

function SessionsTab({ user }: { user: UserDetailDto }) {
  const { t, locale } = useT();
  const api = useApi();
  const { can, config } = useConsole();
  const toast = useToast();
  const text = useGridText();
  const sessions = useQuery({
    queryKey: ["user", user.id, "sessions"],
    queryFn: () => api.users.sessions(user.id),
  });
  const revoke = useMutation({
    mutationFn: (sid: string) => api.users.revokeSession(user.id, sid),
    onSuccess: () => {
      toast({ title: t("user.sessions.revoked"), tone: "success" });
      void sessions.refetch();
    },
  });
  const revokeAll = useMutation({
    mutationFn: () => api.users.revokeSessions(user.id),
    onSuccess: () => void sessions.refetch(),
  });
  const writable = can("users:write");
  const columns = useMemo<GridColDef<SessionDto>[]>(
    () => [
      { field: "userAgent", headerName: t("user.sessions.device"), minWidth: 240 },
      { field: "ip", headerName: "IP", width: 150 },
      {
        field: "createdAt",
        headerName: t("user.sessions.created"),
        type: "dateTime",
        width: 180,
        valueFormatter: (v) => formatDateTime(String(v), locale, config.timeZone),
      },
      {
        field: "expiresAt",
        headerName: t("user.sessions.expires"),
        type: "dateTime",
        width: 180,
        valueFormatter: (v) => formatDateTime(String(v), locale, config.timeZone),
      },
      {
        field: "actions",
        headerName: "",
        type: "actions",
        width: 120,
        align: "right",
        hideable: false,
        disableExport: true,
        renderCell: ({ row: s }) =>
          writable ? (
            <Button variant="text" size="sm" onClick={() => revoke.mutate(s.id)}>
              {t("user.sessions.revoke")}
            </Button>
          ) : null,
      },
    ],
    [t, locale, config.timeZone, writable, revoke],
  );
  if (sessions.isError) return <ErrorBanner error={sessions.error} onRetry={() => void sessions.refetch()} />;
  const items = sessions.data?.items ?? [];
  return (
    <DataGrid
      id="user-sessions"
      label={t("user.tab.sessions")}
      localeText={text}
      rows={items}
      columns={columns}
      loading={sessions.isFetching}
      pagination={false}
      disableColumnFilter
      noRowsContent={<p className="text-sm text-ink-2">{t("user.sessions.empty")}</p>}
      toolbarActions={
        writable &&
        items.length > 0 && (
          <Button
            variant="secondary"
            size="sm"
            loading={revokeAll.isPending}
            onClick={() => revokeAll.mutate()}
          >
            {t("user.sessions.revokeAll")}
          </Button>
        )
      }
    />
  );
}

function DangerZone({ user }: { user: UserDetailDto }) {
  const { t } = useT();
  const api = useApi();
  const { can } = useConsole();
  const toast = useToast();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [banOpen, setBanOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [delOpen, setDelOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["user", user.id] });
    void qc.invalidateQueries({ queryKey: ["users"] });
  };
  const ban = useMutation({
    mutationFn: () => api.users.ban(user.id, reason.trim()),
    onSuccess: () => {
      setBanOpen(false);
      setReason("");
      toast({ title: t("user.banned"), tone: "success" });
      refresh();
    },
  });
  const unban = useMutation({
    mutationFn: () => api.users.unban(user.id),
    onSuccess: () => {
      toast({ title: t("user.unbanned"), tone: "success" });
      refresh();
    },
  });
  const del = useMutation({
    mutationFn: () => api.users.remove(user.id),
    onSuccess: () => {
      toast({ title: t("user.deleted"), tone: "success" });
      void qc.invalidateQueries({ queryKey: ["users"] });
      void navigate({ to: "/users" });
    },
  });
  if (!can("users:write") && !can("users:delete")) return null;
  return (
    <Section title={<span className="text-danger">{t("user.danger")}</span>}>
      <div className="flex flex-wrap gap-2">
        {can("users:write") &&
          (user.status === "banned" ? (
            <Button
              variant="secondary"
              data-testid="unban-button"
              loading={unban.isPending}
              onClick={() => unban.mutate()}
            >
              {t("user.unban")}
            </Button>
          ) : (
            <Button
              variant="outline"
              className="border-danger text-danger hover:bg-danger/5"
              data-testid="ban-button"
              onClick={() => setBanOpen(true)}
            >
              {t("user.ban")}
            </Button>
          ))}
        {can("users:delete") && (
          <Button variant="danger" onClick={() => setDelOpen(true)}>
            {t("user.delete")}
          </Button>
        )}
      </div>
      <ConfirmDialog
        open={banOpen}
        onOpenChange={setBanOpen}
        title={t("user.banTitle", { email: user.email })}
        confirmLabel={t("user.ban")}
        destructive
        loading={ban.isPending}
        confirmDisabled={!reason.trim()}
        confirmTestId="confirm-ban-button"
        onConfirm={() => ban.mutate()}
      >
        <p className="mb-3 text-sm text-ink-2">{t("user.banHint")}</p>
        <Field label={t("user.banReason")} required>
          {(p) => (
            <Textarea {...p} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} />
          )}
        </Field>
        {ban.isError && <Alert tone="error">{errorText(ban.error)}</Alert>}
      </ConfirmDialog>
      <ConfirmDialog
        open={delOpen}
        onOpenChange={setDelOpen}
        title={t("user.deleteTitle", { email: user.email })}
        confirmLabel={t("user.delete")}
        destructive
        loading={del.isPending}
        confirmDisabled={typed.trim().toLowerCase() !== user.email.toLowerCase()}
        onConfirm={() => del.mutate()}
      >
        <p className="mb-3 text-sm">{t("user.deleteConfirm")}</p>
        <Field label={t("user.email")}>
          {(p) => (
            <Input {...p} value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" />
          )}
        </Field>
      </ConfirmDialog>
    </Section>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="text-ink-2">{label}:</span>
      {children}
    </div>
  );
}

export function UserDetailPage() {
  const id = useParams({ strict: false }).id ?? "";
  const { t, locale } = useT();
  const api = useApi();
  const { can, config, me } = useConsole();
  const user = useQuery({ queryKey: ["user", id], queryFn: () => api.users.get(id) });
  const exportData = useMutation({
    mutationFn: () => api.users.exportData(id),
    onSuccess: (data) => downloadText(JSON.stringify(data, null, 2), `user-${id}.json`, "application/json"),
  });
  return (
    <div className="mx-auto flex max-w-[var(--container-content)] flex-col gap-4">
      <Link
        to="/users"
        className="inline-flex items-center gap-1 self-start text-sm text-primary-dark hover:underline"
      >
        <ArrowLeft className="size-4" aria-hidden />
        {t("user.back")}
      </Link>
      {user.isError ? (
        <PageCard>
          <ErrorBanner error={user.error} onRetry={() => void user.refetch()} />
        </PageCard>
      ) : user.isPending ? (
        <Skeleton className="h-40" />
      ) : (
        <>
          <PageCard>
            <div className="flex flex-wrap items-center gap-4 px-6 py-5">
              <Avatar user={user.data} size="size-14" />
              <div className="min-w-0 flex-1 basis-56">
                <h2 className="flex flex-wrap items-center gap-2 text-xl font-medium break-all">
                  {user.data.userCode === "root" ? "root" : user.data.email}
                  {user.data.userCode === "root" && <Badge tone="warning">{t("users.rootBadge")}</Badge>}
                </h2>
                {user.data.userCode === "root" && <p className="text-xs text-ink-2">{t("users.rootHint")}</p>}
                <p className="text-sm text-ink-2">{user.data.displayName}</p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <StatusBadge status={user.data.status} t={t} />
                  <UserTypeBadge type={user.data.userType} />
                  {user.data.roles.map((r) => (
                    <Badge key={r} tone="info">
                      {r}
                    </Badge>
                  ))}
                </div>
              </div>
              {can("data:export") && (
                <Button
                  variant="secondary"
                  loading={exportData.isPending}
                  onClick={() => exportData.mutate()}
                >
                  <Download className="size-4" aria-hidden />
                  {t("user.export")}
                </Button>
              )}
            </div>
            <div className="space-y-1 border-t border-line px-6 py-3">
              <Row label={t("user.id")}>
                <code className="font-mono text-xs">{user.data.id}</code>
                <CopyButton value={user.data.id} />
              </Row>
              {user.data.userCode && (
                <Row label={t("ident.type.user_code")}>
                  <code className="font-mono text-xs" data-testid="user-code">
                    {user.data.userCode}
                  </code>
                  <CopyButton value={user.data.userCode} />
                </Row>
              )}
              {user.data.username && (
                <Row label={t("ident.type.username")}>
                  <code className="font-mono text-xs" data-testid="user-username">
                    {user.data.username}
                  </code>
                  <CopyButton value={user.data.username} />
                </Row>
              )}
              <Row label={t("users.col.created")}>
                {formatDate(user.data.createdAt, locale, config.timeZone)}
              </Row>
              <Row label={t("user.emailVerified")}>
                {user.data.emailVerified ? t("common.yes") : t("common.no")}
              </Row>
              {user.data.banReason && (
                <Alert tone="error" className="mt-2">
                  {t("user.bannedBecause", { reason: user.data.banReason })}
                </Alert>
              )}
            </div>
          </PageCard>
          <PageCard>
            <Tabs.Root defaultValue="profile">
              <Tabs.List label={t("user.tabs")} className="px-2">
                <Tabs.Trigger value="profile">{t("user.tab.profile")}</Tabs.Trigger>
                <Tabs.Trigger value="identifiers">{t("user.tab.identifiers")}</Tabs.Trigger>
                <Tabs.Trigger value="security">{t("user.tab.security")}</Tabs.Trigger>
                <Tabs.Trigger value="access">{t("user.tab.access")}</Tabs.Trigger>
                {can("roles:read") && <Tabs.Trigger value="roles">{t("user.tab.roles")}</Tabs.Trigger>}
                <Tabs.Trigger value="sessions">{t("user.tab.sessions")}</Tabs.Trigger>
                {can("logs:read") && <Tabs.Trigger value="activity">{t("user.tab.activity")}</Tabs.Trigger>}
              </Tabs.List>
              <Tabs.Content value="profile">
                <ProfileTab
                  key={`${user.data.displayName}|${user.data.profile?.updatedAt ?? ""}`}
                  user={user.data}
                />
              </Tabs.Content>
              <Tabs.Content value="identifiers">
                <IdentifiersTab user={user.data} />
              </Tabs.Content>
              <Tabs.Content value="security">
                <SecurityTab user={user.data} />
              </Tabs.Content>
              <Tabs.Content value="access">
                <AccessTab user={user.data} />
              </Tabs.Content>
              <Tabs.Content value="roles">
                <RolesTab user={user.data} />
              </Tabs.Content>
              <Tabs.Content value="sessions">
                <SessionsTab user={user.data} />
              </Tabs.Content>
              <Tabs.Content value="activity">
                <AuditTable subject={id} compact />
              </Tabs.Content>
            </Tabs.Root>
          </PageCard>
          {/* Only root itself may change the root account (the API enforces it too). */}
          {(user.data.userCode !== "root" || me.user.id === user.data.id) && (
            <PageCard>
              <DangerZone user={user.data} />
            </PageCard>
          )}
        </>
      )}
    </div>
  );
}
